import React from 'react';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import * as Inovx from '@shared';
import { CheckBoxComponent, ChangeEventArgs } from '@syncfusion/ej2-react-buttons';
import ViewerActions from '../actions/ViewerActions';
import ComponentHelpers from '../helpers/ComponentHelpers';
import FacilityActions from '../actions/FacilityActions';
import { ListViewComponent } from '@syncfusion/ej2-react-lists';
import FacilitySlideoutSessionPanel from './FacilitySlideoutSessionPanel';
import {
    SessionPanelMeasuredContent,
    SessionPanelToolbar,
    type SessionPanelRenderContext,
} from '../../shared/panels/SessionPanel';
import { FACILITY_SLIDEOUT_PANEL_IDS } from '../helpers/facilitySlideoutPanelSession';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';

const { useState, useEffect, useRef, useMemo, useCallback } = React;
var SystemActions = Inovx.Shared.System.Actions;
declare var InovxLoader: any;
const translateMeasurement = (key: string) => InovxLoader.t(`facility:measurement.${key}`);
const getMeasurementDeleteMessage = (name: string) => InovxLoader.t('facility:message.confirmMeasurementDelete').replace('__name__', name);
const MEASUREMENTS_PANEL_ID = FACILITY_SLIDEOUT_PANEL_IDS.measurements;

interface MeasurementBarProps {
    transientOnly?: boolean;
}

// ---- Child row as a real React component ----
const MeasurementItem = React.memo(function MeasurementItem({
    data,
    transientOnly,
    onToggle,
    onSave,
    onDelete,
    onFocus,
    onNameChange,
}: {
    data: any; // Measurement
    transientOnly?: boolean;
    onToggle: (id: any) => void;
    onSave: (m: any) => void;
    onDelete: (m: any) => void;
    onFocus: (m: any) => void;
    onNameChange: (id: any, name: string, updateStore?: boolean) => void;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState<string>(data.name);
    const inputRef = useRef<HTMLInputElement>(null);
    const clickedButtonRef = useRef<'save' | 'delete' | 'focus' | null>(null);

    const handleNameEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleBlur = () => {
        if (clickedButtonRef.current === 'save') {
            const changed = { ...data, name };
            onSave(changed);
            clickedButtonRef.current = null;
            setIsEditing(false);
            onNameChange(data.id, name);
        } else if (clickedButtonRef.current === 'delete') {
            const message = getMeasurementDeleteMessage(data.name);
            const messageObject = {
                message,
                confirmCallback: async () => onDelete(data),
                cancelCallback: () => { },
            };
            SystemActions.displayYesNoMessage(messageObject);
            clickedButtonRef.current = null;
        } else if (clickedButtonRef.current === 'focus') {
            onFocus(data);
            clickedButtonRef.current = null;
            setIsEditing(false);
            onNameChange(data.id, name);
        } else {
            setIsEditing(false);
            onNameChange(data.id, name);
        }
    };

    const handleClick = () => {
        setIsEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setIsEditing(false);
            setName(data.name);
        } else if (e.key === 'Enter') {
            handleBlur();
        }
        e.stopPropagation();
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.click();
        }
    }, [isEditing]);

    const isActionEnable = () => {
        if (ViewerStore.getSelectedKnowledgeView()) {
            return ComponentHelpers.hasModifyKnowledgeBookPermission(KnowledgeBookStore.getSelectedKnowledgeBook());
        } else {
            return ComponentHelpers.hasManageContentPermission();
        }
    }

    return (
        <div className="list-item">
            <div className="list-item-chk">
                <CheckBoxComponent
                    checked={data.checked}
                    change={() => onToggle(data.id)}
                />
            </div>

            <div className="list-item-title">
                <div className="list-item-title-input">
                    {isEditing ? (
                        <input
                            ref={inputRef}
                            onClick={handleClick}
                            type="text"
                            value={name}
                            onChange={handleNameEdit}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            title={data.name}
                            placeholder={data.name}
                            autoFocus
                        />
                    ) : (
                        <span onClick={handleClick}>{data.name}</span>
                    )}
                </div>

                <div className="list-item-actions">
                    {(isActionEnable()) && !transientOnly && (
                        <button
                            onMouseDown={() => { clickedButtonRef.current = 'save'; }}
                            onClick={() => {
                                setIsEditing(false);
                                if (clickedButtonRef.current) onSave({ ...data, name });
                                clickedButtonRef.current = null;
                            }}
                            title={InovxLoader.t('facility:preferences.saveButton')}
                            disabled={!data.dirty}
                            className="e-lib e-btn e-control e-flat e-icon-btn"
                        >
                            <span className="measurement-save-icon"></span>
                        </button>
                    )}

                    {(isActionEnable()) && (
                        <button
                            onMouseDown={() => { clickedButtonRef.current = 'delete'; }}
                            onClick={() => {
                                setIsEditing(false);
                                if (clickedButtonRef.current) {
                                    const message = getMeasurementDeleteMessage(data.name);
                                    const messageObject = {
                                        message,
                                        confirmCallback: async () => onDelete(data),
                                        cancelCallback: () => { },
                                    };
                                    SystemActions.displayYesNoMessage(messageObject);
                                }
                                clickedButtonRef.current = null;
                            }}
                            title={InovxLoader.t('facility:form.delete')}
                            className="e-lib e-btn e-control e-flat e-icon-btn"
                        >
                            <span className="measurement-delete-icon"></span>
                        </button>
                    )}

                    <button
                        onMouseDown={() => { clickedButtonRef.current = 'focus'; }}
                        onClick={() => {
                            setIsEditing(false);
                            if (clickedButtonRef.current) onFocus(data);
                            clickedButtonRef.current = null;
                        }}
                        title={InovxLoader.t('facility:action.focus')}
                        className="e-lib e-btn e-control e-flat e-icon-btn"
                    >
                        <span className="measurement-focus-icon"></span>
                    </button>
                </div>
            </div>

            {data.measureMode === 'Point' ? (
                <div className="list-item-measurements">
                    <div>
                        <span className="label">{translateMeasurement('north')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.startPosition.z)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                    <div>
                        <span className="label">{translateMeasurement('west')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.startPosition.x)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                    <div>
                        <span className="label">{translateMeasurement('up')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.startPosition.y)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                </div>
            ) : data.measureMode === 'Distance' ? (
                <div className="list-item-distance">
                    <div>
                        <span className="label">{translateMeasurement('distance')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.distance)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                    <div>
                        <span className="label">{translateMeasurement('northDistance')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(Math.abs(data.startPosition.subtract(data.endPosition).z))} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                    <div>
                        <span className="label">{translateMeasurement('eastDistance')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(Math.abs(data.startPosition.subtract(data.endPosition).x))} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                    <div>
                        <span className="label">{translateMeasurement('upDistance')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(Math.abs(data.startPosition.subtract(data.endPosition).y))} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                </div>
            ) : data.measureMode === 'ZOrtho' ? (
                <div className="list-item-distance">
                    <div>
                        <span className="label">{translateMeasurement('distance')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.distance)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                </div>
            ) : data.measureMode === 'YOrtho' ? (
                <div className="list-item-distance">
                    <div>
                        <span className="label">{translateMeasurement('height')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.distance)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="list-item-distance">
                    <div>
                        <span className="label">{translateMeasurement('distance')}</span>
                        <span className="value">
                            {ViewerStore.convertToUserUnit(data.distance)} {ViewerStore.getUnitInformation().Unit}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
});

const MeasurementBar = React.forwardRef((props: MeasurementBarProps, ref) => {
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(FacilityStore.getSidebarOpen());
    const [measurementOpen, setMeasurementOpen] = useState<boolean>(FacilityStore.getMeasurementbarOpen());
    const [facility, setFacility] = useState<IFacilityDetail>(FacilityStore.getSelectedFacility());
    const [measurements, setMeasurements] = useState<any[]>(ViewerStore.getMeasurements());
    const [headerChecked, setHeaderChecked] = useState<boolean>(false);
    const [measurementClass, setMeasurementClass] = useState('');

    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => { isMountedRef.current = false; };
    }, []);

    const hasManageContentPermission = useMemo(
        () => ComponentHelpers.hasManageContentPermission(),
        []
    );

    // ---- Handlers (stable) ----
    const handleCheckboxChange = useCallback((id: any) => {
        const updated = measurements.map(m =>
            m.id === id ? { ...m, checked: !m.checked } : m
        );
        if (!isMountedRef.current) return;
        setMeasurements(updated);
        ViewerActions.updateMeasurements(updated);
    }, [measurements]);

    const handleHeaderCheckboxChange = useCallback((args: ChangeEventArgs) => {
        const isChecked = !!args.checked;
        setHeaderChecked(isChecked);
        const updated = measurements.map(m => ({ ...m, checked: isChecked }));
        setMeasurements(updated);
        ViewerActions.updateMeasurements(updated);
    }, [measurements]);

    const handleNameChange = useCallback((id: any, newName: string, updateStore: boolean = true) => {
        const updated = measurements.map(m =>
            m.id === id ? { ...m, dirty: (m.dirty || m.name !== newName), name: newName } : m
        );
        setMeasurements(updated);
        if (updateStore) ViewerActions.updateMeasurements(updated);
    }, [measurements]);

    const handleDelete = useCallback((measurement: any) => {
        if (measurement.saved) ViewerActions.deleteMeasurement(measurement);
        const updated = measurements.filter(m => m.id !== measurement.id);
        setMeasurements(updated);
        ViewerActions.updateMeasurements(updated);
    }, [measurements]);

    const handleSave = useCallback((measurement: any) => {
        ViewerActions.saveMeasurement(measurement);
    }, []);

    const handleFocus = useCallback((measurement: any) => {
        ViewerActions.focusMeasurement(measurement);
    }, []);

    // ---- Class (open/extend) ----
    useEffect(() => {
        let cls = '';
        if (sidebarOpen && measurementOpen) cls = 'show-extend';
        else if (measurementOpen) cls = 'show';
        setMeasurementClass(cls);
    }, [sidebarOpen, measurementOpen]);

    // ---- Store wiring (top level only) ----
    const updateMeasurementBarList = useCallback(() => {
        if (!isMountedRef.current) return;
        setMeasurements(ViewerStore.getMeasurements());
    }, []);

    useEffect(() => {
        const handleFacilityStoreChange = () => {
            if (!isMountedRef.current) return;
            setSidebarOpen(FacilityStore.getSidebarOpen());
            setMeasurementOpen(FacilityStore.getMeasurementbarOpen());
            setFacility(FacilityStore.getSelectedFacility());
        };

        const handleViewerStoreChange = () => {
            if (!isMountedRef.current) return;
            setMeasurements([...ViewerStore.getMeasurements()]);
        };

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);

        return () => {
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);
        };
    }, []);

    useEffect(() => {
        updateMeasurementBarList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facility, measurementOpen]);

    const handlePanelOpenChange = useCallback((open: boolean) => {
        setMeasurementOpen(open);
        FacilityActions.toggleMeasurementBar(open);
    }, []);

    const renderPanelBody = (panelContext: SessionPanelRenderContext) => (
        <>
            <SessionPanelToolbar
                context={panelContext}
                toolbarClassName="measurement-panel-toolbar"
                listClassName="measurement-panel-toolbar-list"
            >
                <li className="measurement-panel-title">{InovxLoader.t('facility:menu.measurements')}</li>
            </SessionPanelToolbar>

            <div className="measurement-header-options">
                <div className="header-chk">
                    <CheckBoxComponent
                        checked={headerChecked}
                        change={handleHeaderCheckboxChange}
                    />
                </div>
                <div className="header-border"></div>
            </div>

            <SessionPanelMeasuredContent
                bottomPadding={10}
                className="list-view-container"
                fallbackOffset={86}
                minHeight={120}
            >
                {gridHeight => (
                    <div style={{ height: gridHeight, overflowY: 'auto' }}>
                        <ListViewComponent
                            dataSource={measurements as unknown as { [key: string]: Object }[]}
                            template={(item: any) => (
                                <MeasurementItem
                                    data={item}
                                    transientOnly={props.transientOnly}
                                    onToggle={handleCheckboxChange}
                                    onSave={handleSave}
                                    onDelete={handleDelete}
                                    onFocus={handleFocus}
                                    onNameChange={handleNameChange}
                                />
                            ) as any}
                            fields={{ id: 'id', text: 'title' }}
                        />
                    </div>
                )}
            </SessionPanelMeasuredContent>
        </>
    );

    return (
        <FacilitySlideoutSessionPanel
            attachedClassName={measurementClass}
            className="measurement-bar"
            elementId="measurements-bar-content"
            isOpen={measurementOpen}
            onOpenChange={handlePanelOpenChange}
            panelId={MEASUREMENTS_PANEL_ID}
            pinnedWidth={sidebarOpen ? 350 : 280}
        >
            {renderPanelBody}
        </FacilitySlideoutSessionPanel>
    );
});

export default MeasurementBar;
